/**
 * PM Academy -- JS consolidado de la página completa. Reemplaza a los
 * antiguos assets/pm-academy-courses.js y assets/pm-academy-venue-slider.js
 * (ambos quedan sin uso, no eliminados -- ver informe de la ronda de
 * migración completa). Un solo archivo evita proliferación innecesaria:
 * contiene el registro del drawer de fichas (course/company/completed)
 * y los DOS carruseles Flickity de la página (Nuestra Sede, Nuestra
 * historia educativa).
 *
 * Motor de carrusel: Flickity, el mismo asset que Athens ya trae en
 * assets/flickity.pkgd.js (protegido, nunca copiado/renombrado, solo
 * referenciado por URL) y que este proyecto ya usa de forma estable en
 * PM Multibanner y Sliding Panels -- misma arquitectura de carga
 * (ensureFlickity con deduplicación) reutilizada aquí para ambos
 * carruseles. Ningún custom element ni CSS/JS propietario de Concept
 * fue portado.
 *
 * Drawer de ficha: DrawerModal (class global, assets/scripts.js,
 * protegido, sin modificar) + <drawer-toggle> (también nativo, sin
 * modificar) -- mismo patrón ya validado en producción por
 * snippets/quick-view-drawer.liquid + assets/quick-view-drawer.js.
 */
(() => {
  'use strict';

  // ------------------------------------------------------------------
  // Drawer de ficha (cursos vigentes, programas empresa, cursos
  // realizados) -- reutiliza DrawerModal tal cual, el contenido ya está
  // pre-renderizado en el Liquid, sin necesitar ningún override.
  // ------------------------------------------------------------------
  if (!customElements.get('pm-academy-profile-drawer')) {
    customElements.define('pm-academy-profile-drawer', class PmAcademyProfileDrawer extends DrawerModal {});
  }

  // ------------------------------------------------------------------
  // Carga de Flickity compartida entre ambos carruseles de esta página
  // -- una sola promesa de carga, deduplicada, para toda la sección.
  // Corrección aplicada desde el inicio (hallazgo de una auditoría
  // previa sobre PM Multibanner): si la carga falla, flickityLoadPromise
  // se limpia para permitir un reintento real en vez de quedar cacheada
  // como rechazo permanente para el resto de la carga de página.
  // ------------------------------------------------------------------
  var flickityLoadPromise = null;

  function ensureFlickity(scriptUrl) {
    if (window.Flickity) return Promise.resolve(window.Flickity);
    if (flickityLoadPromise) return flickityLoadPromise;

    flickityLoadPromise = new Promise((resolve, reject) => {
      var existing = scriptUrl
        ? document.querySelector('script[src="' + scriptUrl + '"]')
        : null;

      var onLoad = () => {
        if (window.Flickity) resolve(window.Flickity);
        else reject(new Error('pm-academy: Flickity no está disponible tras cargar ' + scriptUrl));
      };
      var onError = () => reject(new Error('pm-academy: error al cargar ' + scriptUrl));

      if (existing) {
        existing.addEventListener('load', onLoad, { once: true });
        existing.addEventListener('error', onError, { once: true });
        return;
      }

      if (!scriptUrl) {
        reject(new Error('pm-academy: falta data-flickity-src'));
        return;
      }

      var script = document.createElement('script');
      script.src = scriptUrl;
      script.defer = true;
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
      document.head.appendChild(script);
    }).catch((error) => {
      flickityLoadPromise = null;
      throw error;
    });

    return flickityLoadPromise;
  }

  // ------------------------------------------------------------------
  // Base compartida por ambos carruseles: descubre carousel/slides por
  // atributos data-* con el prefijo indicado, arma Flickity con
  // opciones comunes y conecta flechas prev/next. Progressive
  // enhancement: si Flickity no llega a cargar, el propio CSS
  // (overflow-x:auto + scroll-snap) sigue funcionando como fallback.
  // ------------------------------------------------------------------
  function definePmAcademyCarousel(tagName, prefix, enhancedClass, cellAlign) {
    if (customElements.get(tagName)) return;

    class PmAcademyCarousel extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;

        this.carousel = this.querySelector('[data-' + prefix + '-carousel]');
        if (!this.carousel) return;

        this.realSlides = Array.prototype.slice.call(
          this.carousel.querySelectorAll(':scope > [data-' + prefix + '-slide]'),
        );
        if (!this.realSlides.length) return;

        this._connected = true;
        this._bootToken = (this._bootToken || 0) + 1;

        this.prevBtn = this.querySelector('[data-' + prefix + '-prev]');
        this.nextBtn = this.querySelector('[data-' + prefix + '-next]');
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-' + prefix + '-dot]'));

        this.autoplayEnabled = this.dataset.autoplay === 'true' && this.realSlides.length > 1;
        this.intervalMs = (parseFloat(this.dataset.interval) || 5) * 1000;
        this.flickity = null;

        var bootToken = this._bootToken;
        ensureFlickity(this.dataset.flickitySrc)
          .then((Flickity) => {
            if (!this._connected || bootToken !== this._bootToken) return;
            this.initFlickity(Flickity);
          })
          .catch(() => {
            // Sin Flickity, el componente permanece en su fallback CSS
            // (scroll-snap nativo) -- no se reintenta indefinidamente ni
            // se construye un segundo motor.
          });
      }

      disconnectedCallback() {
        this._connected = false;
        this._bootToken = (this._bootToken || 0) + 1;

        if (this._resizeRaf) {
          window.cancelAnimationFrame(this._resizeRaf);
          this._resizeRaf = null;
        }

        if (this._onPrevClick && this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrevClick);
        if (this._onNextClick && this.nextBtn) this.nextBtn.removeEventListener('click', this._onNextClick);
        if (this._onDotClick && this.dots) {
          this.dots.forEach((dot) => dot.removeEventListener('click', this._onDotClick));
        }
        if (this._onFlickitySelect && this.flickity) this.flickity.off('select', this._onFlickitySelect);

        if (this.flickity) {
          this.flickity.destroy();
          this.flickity = null;
        }
      }

      // Guardia contra doble inicialización: Flickity.data(elem)
      // devuelve una instancia ya existente en ese elemento -- si
      // connectedCallback llegara a dispararse dos veces, nunca se crea
      // una segunda instancia sobre el mismo carrusel.
      initFlickity(Flickity) {
        if (!this._connected) return;

        this.classList.add(enhancedClass);

        var existing = Flickity.data && Flickity.data(this.carousel);
        if (existing) {
          this.flickity = existing;
        } else {
          var hasMultiple = this.realSlides.length > 1;
          this.flickity = new Flickity(this.carousel, {
            cellSelector: '[data-' + prefix + '-slide]',
            cellAlign: cellAlign || 'left',
            wrapAround: hasMultiple,
            draggable: hasMultiple,
            prevNextButtons: false,
            pageDots: false,
            autoPlay: this.autoplayEnabled ? this.intervalMs : false,
            pauseAutoPlayOnHover: true,
            resize: true,
            accessibility: true,
            // setGallerySize:false -- el contenedor real (.pm-academy-
            // venue-carousel / .pm-academy-timeline-carousel) ya declara
            // su propio height explícito en CSS; dejar que Flickity
            // también intente fijar la altura de .flickity-viewport vía
            // JS (midiendo cell.getSize()) es redundante y, si esa
            // medición ocurre antes de que el layout esté totalmente
            // asentado, puede sobrescribir con un valor incorrecto la
            // altura correcta que ya entrega el CSS. Una sola fuente de
            // verdad para la altura del carrusel: el CSS.
            setGallerySize: false,
          });

          // Flickity mide sus celdas de forma síncrona en el instante de
          // new Flickity(...) -- si eso cae durante un reemplazo de DOM
          // del Shopify Customizer, un único requestAnimationFrame
          // vuelve a medir con dimensiones ya definitivas.
          this._resizeRaf = window.requestAnimationFrame(() => {
            this._resizeRaf = null;
            if (this.flickity) this.flickity.resize();
          });
        }

        this.bindArrows();
        this.bindDots();
      }

      bindArrows() {
        if (this.prevBtn) {
          this._onPrevClick = () => this.flickity.previous();
          this.prevBtn.addEventListener('click', this._onPrevClick);
        }
        if (this.nextBtn) {
          this._onNextClick = () => this.flickity.next();
          this.nextBtn.addEventListener('click', this._onNextClick);
        }
      }

      // Dots (Nuestra Sede) / selector de años (Nuestra historia
      // educativa) -- mismo mecanismo genérico: cada [data-PREFIX-dot]
      // salta a su índice vía la API de Flickity (select) y refleja el
      // slide activo con la clase nativa "is-selected" que Flickity ya
      // aplica sobre cada celda (Cell.prototype.select/unselect,
      // assets/flickity.pkgd.js), evitando mantener un segundo estado
      // "activo" en paralelo.
      bindDots() {
        if (!this.dots.length) return;

        this._onDotClick = (event) => {
          var dot = event.currentTarget;
          var index = this.dots.indexOf(dot);
          if (index > -1) this.flickity.select(index);
        };
        this.dots.forEach((dot) => dot.addEventListener('click', this._onDotClick));

        this._onFlickitySelect = () => {
          var selectedIndex = this.flickity.selectedIndex;
          this.dots.forEach((dot, index) => {
            dot.classList.toggle('is-selected', index === selectedIndex);
            dot.setAttribute('aria-current', index === selectedIndex ? 'true' : 'false');
          });
        };
        this.flickity.on('select', this._onFlickitySelect);
        this._onFlickitySelect();
      }
    }

    customElements.define(tagName, PmAcademyCarousel);
  }

  // "Nuestra Sede": Showroom / Estudio / Live Stage. cellAlign:'left'
  // -- slide único a ancho completo, sin vecinos parciales (full-bleed,
  // igual a la composición hero de Concept).
  definePmAcademyCarousel('pm-academy-venue-slider', 'pm-academy-venue', 'pm-academy-venue--enhanced', 'left');

  // "Nuestra historia educativa": timeline de 7 hitos. cellAlign:'center'
  // -- slide activo centrado con vecinos parciales visibles a los
  // costados (composición editorial, ver assets/pm-academy.css).
  definePmAcademyCarousel('pm-academy-timeline-slider', 'pm-academy-timeline', 'pm-academy-timeline--enhanced', 'center');
})();
